import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { openZendeskMessaging } from "@src/appV2/lib/ZendeskMessaging/openZendeskMessaging";
import { useCallback, useMemo } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { useMissedPunchRequestGeofenceCheck } from "../../hooks/useMissedPunchRequestGeofenceCheck";
import { SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH } from "../../path";
import { SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY } from "../../ShiftDetails/constants";

interface UsePastShiftDetailsActionsParams {
  shiftId: string;
  shiftEnd: string;
  workerId: string;
  workplaceLocation: { latitude: number; longitude: number } | undefined;
  requestId?: string;
  dismissRequest: (requestId: string) => void;
  onWorkerOutsideGeofence: () => void;
}

export function usePastShiftDetailsActions(params: UsePastShiftDetailsActionsParams) {
  const {
    shiftId,
    shiftEnd,
    workerId,
    workplaceLocation,
    requestId,
    dismissRequest,
    onWorkerOutsideGeofence,
  } = params;

  const history = useHistory();
  const { isCheckingGeofence, checkGeofence } = useMissedPunchRequestGeofenceCheck();

  const geofenceParams = useMemo(
    () => ({ shiftId, shiftEnd, workerId, workplaceLocation }),
    [shiftId, shiftEnd, workerId, workplaceLocation]
  );

  const navigateToSubmitTimesheet = useCallback(
    (skipWorkplaceSignature: boolean) => {
      const searchParams = new URLSearchParams({
        [SKIP_WORKPLACE_SIGNATURE_QUERY_PARAM_KEY]: String(skipWorkplaceSignature),
      });
      history.push({
        pathname: generatePath(SUBMIT_UNVERIFIED_TIMESHEET_FULL_PATH, { shiftId }),
        search: searchParams.toString(),
      });
    },
    [history, shiftId]
  );

  const handleGeofenceGatedSubmit = useCallback(
    async (action: string) => {
      logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_ACTION_TAPPED, { shiftId, action });
      const result = await checkGeofence(geofenceParams);
      if (result.status === "inside" || result.status === "unknown") {
        navigateToSubmitTimesheet(false);
      } else {
        logEvent(APP_V2_USER_EVENTS.MPR_RETURN_FOR_SIGNATURE_SHOWN, { shiftId, workerId });
        onWorkerOutsideGeofence();
      }
    },
    [
      checkGeofence,
      geofenceParams,
      navigateToSubmitTimesheet,
      onWorkerOutsideGeofence,
      shiftId,
      workerId,
    ]
  );

  const handleRecheckLocation = useCallback(async () => {
    const result = await checkGeofence(geofenceParams);
    if (result.status === "inside" || result.status === "unknown") {
      navigateToSubmitTimesheet(false);
      return true;
    }

    return false;
  }, [checkGeofence, geofenceParams, navigateToSubmitTimesheet]);

  const onSubmitNewRequest = useCallback(() => {
    void handleGeofenceGatedSubmit("submit_new_request");
  }, [handleGeofenceGatedSubmit]);

  const onRequestPayAdjustment = useCallback(() => {
    void handleGeofenceGatedSubmit("request_pay_adjustment");
  }, [handleGeofenceGatedSubmit]);

  const onAcceptOriginalTimes = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_ACTION_TAPPED, {
      shiftId,
      action: "accept_original_times",
    });
    if (isDefined(requestId)) {
      dismissRequest(requestId);
    }
  }, [shiftId, requestId, dismissRequest]);

  const onContactSupport = useCallback(() => {
    logEvent(APP_V2_USER_EVENTS.PAST_SHIFT_DETAILS_ACTION_TAPPED, {
      shiftId,
      action: "contact_support",
    });
    void openZendeskMessaging();
  }, [shiftId]);

  return {
    onSubmitNewRequest,
    onAcceptOriginalTimes,
    onContactSupport,
    onRequestPayAdjustment,
    handleRecheckLocation,
    navigateToSubmitTimesheet,
    isCheckingGeofence,
  };
}
